import { alpha, SxProps, Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';

/** True when a trailing rule should sit after the heading text (default / left). */
export function isLeftAlignedHeading(textAlign?: CSSProperties['textAlign']): boolean {
  return !textAlign || textAlign === 'left' || textAlign === 'start' || textAlign === 'justify';
}

const headingRuleColor = alpha('#9C5A37', 0.5);

/** Nudge the rule down from flex center to the middle of lowercase (x-height). */
const displayHeadingTrailingRuleLineOffset = '0.22em';

/** Outer h2/h3 row: full width inside flex columns, text + trailing line. */
export const displayHeadingTrailingRuleContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  width: '100%',
  maxWidth: '100%',
  alignSelf: 'stretch',
  minWidth: 0,
  boxSizing: 'border-box',
};

export const displayHeadingTrailingRuleTextSx: SxProps<Theme> = {
  flex: '0 1 auto',
  minWidth: 0,
  mb: 0,
};

export const displayHeadingTrailingRuleLineSx: SxProps<Theme> = {
  flex: '1 1 auto',
  minWidth: 24,
  height: '1px',
  bgcolor: headingRuleColor,
  alignSelf: 'center',
  transform: `translateY(${displayHeadingTrailingRuleLineOffset})`,
};

/** @deprecated Prefer DisplayHeadingWithRule; kept for theme variant overrides on plain Typography. */
export const displayHeadingTrailingRuleSx: SxProps<Theme> = {
  ...displayHeadingTrailingRuleContainerSx,
  '&::after': {
    content: '""',
    flex: '1 1 auto',
    minWidth: 24,
    height: '1px',
    bgcolor: headingRuleColor,
    alignSelf: 'center',
    transform: `translateY(${displayHeadingTrailingRuleLineOffset})`,
  },
};

/** TinyMCE iframe preview — mirrors display heading rule for raw h2/h3. */
export const tinymceHeadingRuleContentCss = `
  h2, h3 {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    max-width: 100%;
    align-self: stretch;
    box-sizing: border-box;
  }
  h2::after, h3::after {
    content: "";
    flex: 1 1 auto;
    min-width: 24px;
    height: 1px;
    background-color: ${headingRuleColor};
    transform: translateY(${displayHeadingTrailingRuleLineOffset});
  }
  h2[align="center"], h2[align="right"], h3[align="center"], h3[align="right"],
  h2[style*="text-align: center"], h2[style*="text-align: right"],
  h2[style*="text-align:center"], h2[style*="text-align:right"],
  h3[style*="text-align: center"], h3[style*="text-align: right"],
  h3[style*="text-align:center"], h3[style*="text-align:right"] {
    display: block;
  }
  h2[align="center"]::after, h2[align="right"]::after,
  h3[align="center"]::after, h3[align="right"]::after,
  h2[style*="text-align: center"]::after, h2[style*="text-align: right"]::after,
  h2[style*="text-align:center"]::after, h2[style*="text-align:right"]::after,
  h3[style*="text-align: center"]::after, h3[style*="text-align: right"]::after,
  h3[style*="text-align:center"]::after, h3[style*="text-align:right"]::after {
    content: none;
    display: none;
  }
`;
